import { Eye, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useImpersonation } from "@/hooks/use-impersonation";
import { useSession } from "@/lib/auth-client";

/** Plain-language names for the roles a viewed person can hold. */
const ROLE_LABELS: Record<string, string> = {
  admin: "an admin",
  admin_readonly: "a view-only admin",
  manager: "a manager",
  user: "a standard user",
};

/**
 * Always-visible reminder that the screen is someone else's. Sits above the
 * header on every dashboard page so it can't be scrolled away; the one
 * control is the way back.
 *
 * It names the ROLE as well as the person because the two are separable and
 * the difference is invisible otherwise: someone who has never signed in is
 * viewed as a standard user (WI-150) whatever they do in real life, so a
 * people-manager viewed that way shows no My Team. Saying "as a standard user"
 * is what stops that reading as a fault instead of as the role — an admin fixes
 * it by assigning the role on the Users page.
 */
export function ImpersonationBanner() {
  const { active, stop, isPending } = useImpersonation();
  const { data: session } = useSession();
  // An unrecognised role says nothing rather than guessing a label — a role
  // added by a migration newer than this build must not be mislabelled.
  const role = session?.user?.role;
  const roleLabel = typeof role === "string" ? (ROLE_LABELS[role] ?? null) : null;
  if (!active || !session?.user) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-between gap-3 border-b border-warning/40 bg-warning-bg px-4 py-2 text-sm md:px-6"
    >
      <p className="flex min-w-0 items-center gap-2">
        <Eye className="size-4 shrink-0" />
        <span className="truncate">
          You're viewing Enable as <span className="font-medium">{session.user.name}</span>
          {roleLabel === null ? null : <> · {roleLabel}</>}. Changes are recorded under your name.
        </span>
      </p>
      <Button size="sm" variant="outline" onClick={() => void stop()} disabled={isPending}>
        {isPending ? <Loader2 className="mr-1.5 size-3.5 animate-spin" /> : null}
        Stop viewing as
      </Button>
    </div>
  );
}
